import { debounce } from "lodash";
import * as React from "react";
import { FC, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { Form, actions } from "react-redux-form";
import { useLocation, useParams } from "react-router";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import * as NavigationActions from "../../../actions/navigation";
import * as ProjectActions from "../../../actions/project";
import * as ProjectInvestigatorActions from "../../../actions/projectInvestigator";
import * as TrialActions from "../../../actions/trial";
import { NotificationAlert } from "../../../components/common/index";
import {
    CheckboxFormInput,
    SelectFormInput,
} from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { createPersonnelSearchLoader } from "../../../functions/personnel";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import * as AuthenticationHelper from "../../../helpers/authentication";
import {
    ProjectInvestigatorsBreadcrumbs,
    ProjectInvestigatorsCreateBreadcrumbs,
    TrialProjectInvestigatorsBreadcrumbs,
} from "../../../helpers/breadcrumbHelper";
import * as FormHelper from "../../../helpers/formHelper";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import {
    ProjectSubMenu,
    TrialProjectSubMenu,
} from "../../../helpers/subMenuHelper";
import useNavigate from "../../../hooks/useNavigate";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { ProjectLayout } from "../../index";

import "./ProjectInvestigatorPage.scss";

interface ProjectInvestigatorPageProps {
    form: Dtos.ProjectInvestigator & IRegistrationFormState;
    formState: Dtos.FormState;
    formProperties: Dtos.FormProperty[];
    projectInvestigator: Dtos.ProjectInvestigator;
    project: Dtos.Project;
    lookups: Dtos.Lookup[];
    trial: Dtos.Trial;
    loadingProject: boolean;
    loadProjectSuccess: boolean;
    loadProjectFailure: boolean;
    loadProjectErrorCode: string;
    loadingTrial: boolean;
    loadTrialSuccess: boolean;
    loadTrialFailure: boolean;
    loadTrialErrorCode: string;
    loadingProjectInvestigator: boolean;
    loadProjectInvestigatorSuccess: boolean;
    loadProjectInvestigatorFailure: boolean;
    loadProjectInvestigatorErrorCode: string;
    savingProjectInvestigator: boolean;
    saveProjectInvestigatorSuccess: boolean;
    saveProjectInvestigatorFailure: boolean;
    creatingProjectInvestigator: boolean;
    validatingProjectInvestigator: boolean;
    validationFailures: Dtos.ResponseError[];
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearProjectInvestigator: ProjectInvestigatorActions.IProjectInvestigatorClearActionCreator;
    loadProjectInvestigator: ProjectInvestigatorActions.IProjectInvestigatorLoadByIdActionCreator;
    saveProjectInvestigator: ProjectInvestigatorActions.IProjectInvestigatorSaveActionCreator;
    createProjectInvestigator: ProjectInvestigatorActions.IProjectInvestigatorCreateActionCreator;
    validateProjectInvestigator: ProjectInvestigatorActions.IProjectInvestigatorValidateActionCreator;
    clearProject: ProjectActions.IProjectClearActionCreator;
    loadProject: ProjectActions.IProjectLoadByIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    clearTrial: TrialActions.ITrialClearActionCreator;
    loadTrial: TrialActions.ITrialLoadByIdActionCreator;
    loadForm: typeof actions.load;
    changeForm: typeof actions.change;
    resetForm: typeof actions.reset;
}

const reduxFormName: string = "reduxForms.projectInvestigator";

const ProjectInvestigatorPage: FC<ProjectInvestigatorPageProps> = ({
    form,
    formState,
    formProperties,
    projectInvestigator,
    project,
    lookups,
    trial,
    loadingProject,
    loadProjectFailure,
    loadProjectErrorCode,
    loadingTrial,
    loadTrialFailure,
    loadTrialErrorCode,
    loadingProjectInvestigator,
    loadProjectInvestigatorFailure,
    loadProjectInvestigatorErrorCode,
    savingProjectInvestigator,
    saveProjectInvestigatorSuccess,
    saveProjectInvestigatorFailure,
    creatingProjectInvestigator,
    validatingProjectInvestigator,
    validationFailures,
    permissions,
    clearProjectInvestigator,
    loadProjectInvestigator,
    saveProjectInvestigator,
    createProjectInvestigator,
    validateProjectInvestigator,
    clearProject,
    loadProject,
    setBreadcrumbs,
    setDrawerItems,
    clearTrial,
    loadTrial,
    loadForm,
    changeForm,
    resetForm
}) => {
    //
    // -- Params --
    const { projectId } = useParams<{ projectId: string; }>();
    const { projectInvestigatorId } = useParams<{ projectInvestigatorId: string; }>();
    const projectIdNumber = parseInt(projectId, 10);
    const projectInvestigatorIdNumber = parseInt(projectInvestigatorId, 10);

    //
    // -- Hooks --
    const location = useLocation();
    const navigate = useNavigate();

    //
    // -- States --
    const [saveAndReturn, setSaveAndReturn] = useState<boolean>(false);
    const [initialsed, setInitalised] = useState<boolean>(false);
    const [hasBeenSubmitted, setHasBeenSubmitted] = useState<boolean>(false);

    const loadReduxForm = () => {
        let extraData: Dtos.ProjectInvestigator =
            {} as Dtos.ProjectInvestigator;

        loadForm(
            reduxFormName,
            Object.assign({ ...projectInvestigator }, extraData)
        );
    };


    // Load or create trial/project
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (projectInvestigatorIdNumber && !projectInvestigator && !loadingProjectInvestigator) {
            if (baseRoute === "trials" && !trial) {
                setDrawerItems(
                    TrialProjectSubMenu(projectIdNumber, permissions)
                );
                setBreadcrumbs(
                    TrialProjectInvestigatorsBreadcrumbs(
                        projectIdNumber,
                        projectInvestigatorIdNumber
                    )
                );
                loadTrial(projectIdNumber);
                loadProjectInvestigator(projectInvestigatorIdNumber);
            } else if (baseRoute !== "trials" && !project && !loadingProjectInvestigator) {
                setDrawerItems(ProjectSubMenu(projectIdNumber, permissions));
                setBreadcrumbs(
                    ProjectInvestigatorsBreadcrumbs(
                        projectIdNumber,
                        projectInvestigatorIdNumber
                    )
                );
                loadProject(projectIdNumber);
                loadProjectInvestigator(projectInvestigatorIdNumber);
            }
        } else if (!projectInvestigatorIdNumber && !projectInvestigator && !creatingProjectInvestigator) {
            setDrawerItems([]);
            setBreadcrumbs(
                ProjectInvestigatorsCreateBreadcrumbs(projectIdNumber)
            );
            createProjectInvestigator(0, projectIdNumber);
        } else if (projectInvestigator && !initialsed) {
            loadReduxForm();
            setInitalised(true);
        }
    }, [
        projectInvestigatorIdNumber,
        projectIdNumber,
        permissions,
        trial,
        project,
        projectInvestigator,
        initialsed,
        creatingProjectInvestigator,
        loadingProjectInvestigator,
        setDrawerItems,
        setBreadcrumbs,
        loadTrial,
        loadProject,
        loadProjectInvestigator,
        createProjectInvestigator,
        loadReduxForm,
        setInitalised,
        TrialProjectSubMenu,
        TrialProjectInvestigatorsBreadcrumbs,
        ProjectSubMenu,
        ProjectInvestigatorsBreadcrumbs,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadProjectFailure) {
            navigate(`/error/${loadProjectErrorCode}`);
        }
        if (loadTrialFailure) {
            navigate(`/error/${loadTrialErrorCode}`);
        }
        if (loadProjectInvestigatorFailure) {
            navigate(`/error/${loadProjectInvestigatorErrorCode}`);
        }
    }, [
        loadProjectFailure,
        loadTrialFailure,
        loadProjectInvestigatorFailure,
        loadProjectErrorCode,
        loadTrialErrorCode,
        loadProjectInvestigatorErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        const baseRoute = location?.pathname?.split("/")[1];

        if (
            baseRoute === "trials" &&
            trial?.displayName &&
            trial?.projectProtocolID
        ) {
            setDrawerItems(TrialProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(
                TrialProjectInvestigatorsBreadcrumbs(
                    projectIdNumber,
                    projectInvestigatorIdNumber,
                    trial.displayName,
                    trial.projectProtocolID,
                    projectInvestigator?.personnel?.displayWithSalutation
                )
            );
        } else if (projectIdNumber && project?.projectShortTitle) {
            setDrawerItems(ProjectSubMenu(projectIdNumber, permissions));
            setBreadcrumbs(
                ProjectInvestigatorsBreadcrumbs(
                    projectIdNumber,
                    projectInvestigatorIdNumber,
                    project.projectShortTitle,
                    projectInvestigator?.personnel?.displayWithSalutation
                )
            );
        }
    }, [
        projectIdNumber,
        projectInvestigatorIdNumber,
        project,
        permissions,
        trial,
        location,
        projectInvestigator,
        setDrawerItems,
        setBreadcrumbs,
        ProjectSubMenu,
        TrialProjectInvestigatorsBreadcrumbs,
        TrialProjectSubMenu,
        ProjectInvestigatorsBreadcrumbs,
    ]);

    const handleOnSubmit = async (data: any) => {
        if (!hasBeenSubmitted) {
            setHasBeenSubmitted(true);
        }

        if (data) {
            let changes: Dtos.ProjectInvestigator =
                {} as Dtos.ProjectInvestigator;

            await saveProjectInvestigator(Object.assign({ ...data }, changes));

            if (data.saveAndReturn) {
                setSaveAndReturn(true);
            }
        }
    };

    const handleOnSubmitFailed = (_data: Dtos.ProjectInvestigator) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        const baseRoute = location?.pathname?.split("/")[1];
        navigate(
            baseRoute === "trials"
                ? `/trials/${projectIdNumber}/project/details`
                : `/projects/${projectIdNumber}/details`
        );
    };

    // Failed saving
    useEffect(() => {
        if (!savingProjectInvestigator && saveProjectInvestigatorFailure) {
            Alert.error(
                <NotificationAlert
                    alertContent={renderRegistrationFormSaveBlock()}
                    icon={FontAwesomeIcons.Solid.BAN}
                />
            );
        }
    }, [
        savingProjectInvestigator,
        saveProjectInvestigatorSuccess,
        loadProjectInvestigator,
    ]);

    // Reload project investigator after saving
    useEffect(() => {
        if (!savingProjectInvestigator && saveProjectInvestigatorSuccess) {
            Alert.success(
                <NotificationAlert
                    alertContent={<span>Project Investigator saved successfully</span>}
                    icon={FontAwesomeIcons.Solid.CHECK}
                />
            );

            const baseRoute = location?.pathname?.split("/")[1];
            if (saveAndReturn) {
                let url: string =
                    baseRoute === "trials"
                        ? `/trials/${projectIdNumber}/project/details`
                        : `/projects/${projectIdNumber}/details`;
                navigate(url);
                clearProjectInvestigator();
                clearProject();
                clearTrial();
            } else if (!projectInvestigatorIdNumber) {
                let url: string =
                    baseRoute === "trials"
                        ? `/trials/${trial.id}/project/investigators/${projectInvestigator?.id}/details`
                        : `/projects/${project.id}/investigators/${projectInvestigator?.id}/details`;
                navigate(url);
                clearProjectInvestigator();
                clearProject();
                clearTrial();
            } else {
                loadProjectInvestigator(projectInvestigatorIdNumber);
                loadProject(projectIdNumber);
            }
        }
    }, [
        savingProjectInvestigator,
        saveProjectInvestigatorSuccess,
        loadProjectInvestigator,
        projectInvestigatorIdNumber,
        projectIdNumber,
        navigate,
        clearProjectInvestigator,
        saveAndReturn,
    ]);

    // Cleanup effect to clear the project investigators on component unmount
    useEffect(() => {
        return () => {
            clearProjectInvestigator();
            clearProject();
            clearTrial();
            resetForm(reduxFormName);
        };
    }, [
        clearProjectInvestigator,
        clearProject,
        clearTrial,
        resetForm,
        reduxFormName,
    ]);

    // Debounce validation after the initial submit to clear form validation errors as the form get updated.
    useEffect(() => {
        const debounceValidate = debounce(async () => {
            let changes: Dtos.ProjectInvestigator =
                {} as Dtos.ProjectInvestigator;

            if (form && hasBeenSubmitted && !validatingProjectInvestigator) {
                await validateProjectInvestigator(
                    Object.assign({ ...form }, changes)
                );
            }
        }, 250);

        debounceValidate();

        return () => {
            debounceValidate.cancel();
        };
    }, [form]);

    const personnelSearchLoader = useMemo(() => {
        return createPersonnelSearchLoader(
            form?.personnel 
                ? { label: form?.personnel?.displayWithSalutation, value: form?.personnelId } as Dtos.PersonnelOption 
                : null
        );
    }, [form]);

    const renderContent = useMemo(() => {
        const baseRoute = location?.pathname?.split("/")[1];
        const hasPermission: boolean =
            AuthenticationHelper.hasCreateEditPermission(
                form,
                permissions,
                Dtos.Permission.ProjectInvestigatorsUpdate,
                Dtos.Permission.ProjectInvestigatorsCreate
            );
        const formDisabled: boolean =
            !hasPermission ||
            savingProjectInvestigator ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled) ||
            baseRoute === "trials";
        const formNew: boolean = FormHelper.isNew(formState, form);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            detailsGroup: {
                type: RegistrationComponent.RegistrationElement,
                component: (
                    <>
                        <div className="card card-shadow" style={{ zIndex: 1 }}>
                            <div className="form-row py-4 px-4">
                                <div className="col-md-10">
                                    {renderFormControls(
                                        form,
                                        "projectInvestigator",
                                        {
                                            personnelId: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: SelectFormInput,
                                                inputDisabled: formDisabled || !formNew,
                                                inputProps: {
                                                    value: form?.personnelId ?? null,
                                                    cacheOptions: true,
                                                    defaultOptions: form?.personnel
                                                        ? [{ label: form.personnel.displayWithSalutation, value: form.personnel.id }]
                                                        : true,
                                                    placeholder: "Type to search for personnel...",
                                                    clearable: true,
                                                    loadOptions: personnelSearchLoader,
                                                },
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-2",
                                                fieldClassName: "col-sm-10 generic-form-field",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                                <div className="col-md-2 d-flex align-items-center">
                                    {renderFormControls(
                                        form,
                                        "projectInvestigator",
                                        {
                                            active: {
                                                type: RegistrationComponent.RegistrationControl,
                                                inputType: CheckboxFormInput,
                                                inputDisabled: formDisabled,
                                                inputProps: {
                                                    label: "Active",
                                                },
                                                inputLabel: " ",
                                                groupClassName: "form-row bottom-0 border-top-0 pb-0 pt-0",
                                                labelClassName: "col-form-label col-sm-6",
                                                fieldClassName: "col-sm-6 generic-form-field d-flex align-items-center",
                                            },
                                        },
                                        lookups,
                                        validationFailures,
                                        formProperties,
                                        changeForm
                                    )}
                                </div>
                            </div>
                        </div>
                    </>
                ),
            },
        };

        return (
            <div>
                <h1>Project Investigator</h1>
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) => handleOnSubmit(val)}
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "projectInvestigator",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    false
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>

                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit And Return
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [form, permissions, savingProjectInvestigator, formState, personnelSearchLoader]);

    return (
        <ProjectLayout
            loading={
                loadingProject ||
                loadingProjectInvestigator ||
                savingProjectInvestigator ||
                creatingProjectInvestigator ||
                loadingTrial
            }
            permission={Dtos.Permission.ProjectInvestigatorsView}
        >
            {renderContent}
        </ProjectLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<ProjectInvestigatorPageProps> => {
    let projectInvestigator: Dtos.ProjectInvestigator | undefined = !(
        state.projectInvestigator.formData instanceof Array
    )
        ? state.projectInvestigator.formData
        : undefined;
    let project: Dtos.Project | undefined = !(
        state.projects.formData instanceof Array
    )
        ? state.projects.formData
        : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];
    let trial: Dtos.Trial | undefined = !(
        state.trials.formData instanceof Array
    )
        ? state.trials.formData
        : undefined;

    return {
        projectInvestigator: projectInvestigator!,
        project: project!,
        trial: trial!,
        loadingProjectInvestigator:
            state.projectInvestigator.loadState &&
            state.projectInvestigator.loadState.status == RequestState.Pending,
        loadProjectInvestigatorSuccess:
            state.projectInvestigator.loadState &&
            state.projectInvestigator.loadState.status == RequestState.Success,
        loadProjectInvestigatorFailure:
            state.projectInvestigator.loadState &&
            state.projectInvestigator.loadState.status == RequestState.Failure,
        loadProjectInvestigatorErrorCode:
            state.projectInvestigator.loadState.errorCode,
        savingProjectInvestigator:
            state.projectInvestigator.saveState &&
            state.projectInvestigator.saveState.status == RequestState.Pending,
        saveProjectInvestigatorSuccess:
            state.projectInvestigator.saveState &&
            state.projectInvestigator.saveState.status == RequestState.Success,
        saveProjectInvestigatorFailure:
            state.projectInvestigator.saveState &&
            state.projectInvestigator.saveState.status == RequestState.Failure,
        creatingProjectInvestigator:
            state.projectInvestigator.createState &&
            state.projectInvestigator.createState.status ==
                RequestState.Pending,
        validatingProjectInvestigator:
            state.projectInvestigator.validateState &&
            state.projectInvestigator.validateState.status ==
                RequestState.Pending,
        loadingProject:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Pending,
        loadProjectSuccess:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Success,
        loadProjectFailure:
            state.projects.loadState &&
            state.projects.loadState.status == RequestState.Failure,
        loadProjectErrorCode: state.projects.loadState.errorCode,
        loadingTrial:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Pending,
        loadTrialSuccess:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Success,
        loadTrialFailure:
            state.trials.loadState &&
            state.trials.loadState.status == RequestState.Failure,
        loadTrialErrorCode: state.trials.loadState.errorCode,
        permissions: permissions,
        lookups: state.projectInvestigator.lookups,
        form: state.reduxForms.projectInvestigator,
        formState: state.projectInvestigator.formState,
        formProperties: state.projectInvestigator.formProperties,
        validationFailures: projectInvestigator
            ? state.projectInvestigator.validationFailures
            : undefined,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<ProjectInvestigatorPageProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        resetForm: bindActionCreators(actions.reset, dispatch),
        loadProjectInvestigator: bindActionCreators(
            ProjectInvestigatorActions.LoadProjectInvestigatorById,
            dispatch
        ),
        clearProjectInvestigator: bindActionCreators(
            ProjectInvestigatorActions.Clear,
            dispatch
        ),
        createProjectInvestigator: bindActionCreators(
            ProjectInvestigatorActions.CreateProjectInvestigator,
            dispatch
        ),
        validateProjectInvestigator: bindActionCreators(
            ProjectInvestigatorActions.ValidateProjectInvestigator,
            dispatch
        ),
        saveProjectInvestigator: bindActionCreators(
            ProjectInvestigatorActions.SaveProjectInvestigator,
            dispatch
        ),
        loadProject: bindActionCreators(
            ProjectActions.LoadProjectById,
            dispatch
        ),
        clearProject: bindActionCreators(ProjectActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        loadTrial: bindActionCreators(TrialActions.LoadTrialById, dispatch),
        clearTrial: bindActionCreators(TrialActions.Clear, dispatch)
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ProjectInvestigatorPage);
